import { z } from 'zod';
import { patchSchema } from './common.ts';

/**
 * Constructor documents — the contract between the editor, the viewer and the API.
 * Source: docs/design/02-constructor.md §9 (data model), §4 (widgets), §6 (sections), §8 (coordinates).
 *
 *  - Dashboard ("Bosh panel", one per tenant): JSONB `DashboardDocument` — a 1920×1080 stage with a background
 *    and free-positioned elements; array order = z-order (0 = back).
 *  - Section ("Bo'lim"): relational row (card fields) with folders ("papka"/"modifikasiya").
 *  - Folder: relational row + JSONB `FolderDocument` — drawing, pins (normalized u/v), layers.
 *  - Scene3D: JSONB `Scene3DDocument` per section (Google Earth / Cesium placement + sensor anchors).
 *
 * "Sensor" in this file = one measured channel = an `indicators` row (Ko'rsatkich) of the device registry.
 * Every document has `schemaVersion`; rows carry `version` for optimistic locking.
 */

export const SCHEMA_VERSION = 1 as const;
/** @deprecated use SCHEMA_VERSION */
export const CURRENT_SCHEMA_VERSION = SCHEMA_VERSION;

/* ───────── primitives ───────── */

export const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'HEX rang (#RRGGBB)');
/** Remix Icon name, e.g. `drop-line` (rendered with @remixicon/react). */
export const remixIconSchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).max(48);
/** Id of an item inside a document (nanoid). */
export const itemIdSchema = z.string().min(1).max(40);

export const FILE_REF_KINDS = ['image', 'svg', 'gif', 'video', 'dwg', 'glb', 'obj-bundle', 'pdf', 'icon'] as const;
export const fileRefSchema = z.object({
  fileId: z.uuid(),
  kind: z.enum(FILE_REF_KINDS),
  name: z.string().max(255),
  mime: z.string().max(120),
  sizeBytes: z.number().int().nonnegative(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  durationSec: z.number().nonnegative().optional(),
});
export type FileRef = z.infer<typeof fileRefSchema>;

export const iconRefSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('preset'), name: remixIconSchema }),
  z.object({ kind: z.literal('upload'), file: fileRefSchema }),
]);
export type IconRef = z.infer<typeof iconRefSchema>;

/* ───────── live data bindings ───────── */

export const SENSOR_STATUSES = ['normal', 'warning', 'danger', 'offline', 'unknown'] as const;
export type SensorStatus = (typeof SENSOR_STATUSES)[number];

export const sensorBindingSchema = z.object({
  /** indicators.id */
  sensorId: z.uuid(),
  /** display fallback, refreshed by the API on every save */
  snapshot: z.object({
    code: z.string().max(64),
    name: z.string().max(160),
    unit: z.string().max(32),
    deviceCode: z.string().max(64).nullable(),
    deviceType: z.string().max(160).nullable(),
  }),
  decimals: z.number().int().min(0).max(6).optional(),
  unitOverride: z.string().max(12).optional(),
});
export type SensorBinding = z.infer<typeof sensorBindingSchema>;

export const valueSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('manual'), value: z.string().max(60) }),
  z.object({ kind: z.literal('sensor'), binding: sensorBindingSchema }),
]);
export type ValueSource = z.infer<typeof valueSourceSchema>;

/* ───────── dashboard ───────── */

export const STAGE = { width: 1920, height: 1080 } as const;

export const dashboardBackgroundSchema = z.object({
  /** image | svg | video ("Chizma" palette item) */
  media: fileRefSchema.nullable(),
  opacity: z.number().min(0.1).max(1),
  scale: z.number().min(1).max(3),
});
export type DashboardBackground = z.infer<typeof dashboardBackgroundSchema>;

const elementBase = z.object({
  id: itemIdSchema,
  name: z.string().max(80),
  /** stage px, top-left (marker: dot centre) */
  x: z.number().min(-4000).max(8000),
  y: z.number().min(-4000).max(8000),
  hidden: z.boolean(),
  locked: z.boolean(),
});

export const ASPECT_RATIOS = ['16:9', '4:3', '1:1'] as const;
const mediaFields = {
  media: fileRefSchema.nullable(),
  aspect: z.enum(ASPECT_RATIOS),
  width: z.number().min(80).max(960),
  zoom: z.number().min(1).max(3),
};

export const DATE_FORMATS = ['d MMMM yyyy', 'dd.MM.yyyy'] as const;

export const dashboardElementSchema = z.discriminatedUnion('type', [
  elementBase.extend({ type: z.literal('clock'), scale: z.number().min(0.5).max(1.5), showSeconds: z.boolean() }),
  elementBase.extend({ type: z.literal('date'), scale: z.number().min(0.5).max(1.5), format: z.enum(DATE_FORMATS) }),
  elementBase.extend({
    type: z.literal('indicator'),
    scale: z.number().min(0.5).max(1.5),
    icon: remixIconSchema,
    label: z.string().max(40),
    source: valueSourceSchema,
    unit: z.string().max(12),
  }),
  elementBase.extend({
    type: z.literal('title'),
    title: z.string().max(120),
    titleSize: z.number().min(12).max(48),
    subtitle: z.string().max(160),
    subtitleSize: z.number().min(10).max(25),
    showAccentBar: z.boolean(),
    /** show the organization logo before the title (viewer top-left block, 14:7285) */
    showLogo: z.boolean().optional(),
  }),
  elementBase.extend({
    type: z.literal('marker'),
    label: z.string().max(60),
    scale: z.number().min(0.5).max(1.5),
    linkSectionId: z.uuid().nullable(),
    /** label position relative to the pin; `auto` flips on stage overflow / collisions */
    labelSide: z.enum(['auto', 'left', 'right']).optional(),
  }),
  elementBase.extend({ type: z.literal('image'), ...mediaFields }),
  elementBase.extend({ type: z.literal('gif'), ...mediaFields }),
  elementBase.extend({ type: z.literal('video'), ...mediaFields, autoplayMuted: z.boolean(), loop: z.boolean() }),
  /** the "Bo'limlar" card grid — exactly one per dashboard */
  elementBase.extend({
    type: z.literal('sectionGrid'),
    dock: z.enum(['bottom', 'free']),
    width: z.number().min(200).max(1920),
    columns: z.number().int().min(1).max(8),
    gap: z.number().min(0).max(64),
    /** vertical gap between card rows (defaults to `gap`) */
    rowGap: z.number().min(0).max(64).optional(),
    cardHeight: z.number().min(48).max(240),
  }),
]);
export type DashboardElement = z.infer<typeof dashboardElementSchema>;
export type ElementType = DashboardElement['type'];
export type ElementOf<T extends ElementType> = Extract<DashboardElement, { type: T }>;

export const dashboardDocumentSchema = z
  .object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    stage: z.object({ width: z.literal(STAGE.width), height: z.literal(STAGE.height) }),
    background: dashboardBackgroundSchema,
    elements: z.array(dashboardElementSchema).max(300),
  })
  .superRefine((doc, ctx) => {
    const ids = new Set<string>();
    doc.elements.forEach((el, i) => {
      if (ids.has(el.id)) ctx.addIssue({ code: 'custom', path: ['elements', i, 'id'], message: 'Takroriy id' });
      ids.add(el.id);
    });
    if (doc.elements.filter((e) => e.type === 'sectionGrid').length > 1) {
      ctx.addIssue({ code: 'custom', path: ['elements'], message: "Bo'limlar to'ri faqat bitta bo'lishi mumkin" });
    }
  });
export type DashboardDocument = z.infer<typeof dashboardDocumentSchema>;

/* ───────── folders (inside a section) ───────── */

export const drawingSchema = z.object({
  /** displayed file (SVG/PNG/JPG/WEBP); DWG is converted to SVG server-side (later) */
  file: fileRefSchema,
  source: fileRefSchema.nullable(),
  naturalWidth: z.number().positive().max(16384),
  naturalHeight: z.number().positive().max(16384),
  opacity: z.number().min(0.1).max(1),
  scale: z.number().min(0.25).max(4),
});
export type Drawing = z.infer<typeof drawingSchema>;

export const LABEL_SIDES = ['top', 'right', 'bottom', 'left'] as const;
export type LabelSide = (typeof LABEL_SIDES)[number];
export const labelRowSchema = z.object({ id: itemIdSchema, name: z.string().max(40), source: valueSourceSchema });
export type LabelRow = z.infer<typeof labelRowSchema>;

export const pinSchema = z.object({
  id: itemIdSchema,
  /** position on the drawing, 0..1 of its natural size */
  at: z.object({ u: z.number().min(0).max(1), v: z.number().min(0).max(1) }),
  /** QURILMA — the sensor that drives the status colour */
  primary: sensorBindingSchema.nullable(),
  title: z.string().max(60).nullable(),
  rows: z.array(labelRowSchema).max(8),
  /** label placement in design px */
  label: z.object({
    side: z.enum(LABEL_SIDES),
    distance: z.number().min(0).max(100),
    offset: z.number().min(-50).max(50),
  }),
  hidden: z.boolean(),
});
export type Pin = z.infer<typeof pinSchema>;

export const deviceInfoLayerSchema = z.object({
  enabled: z.boolean(),
  title: z.string().max(120),
  media: z.array(z.object({ id: itemIdSchema, file: fileRefSchema })).max(12),
  collage: z.enum(['grid', 'mosaic', 'carousel']),
  /** rich-text editor JSON (ProseMirror/TipTap doc) */
  description: z.object({ type: z.literal('doc'), content: z.array(z.unknown()) }),
  descriptionChars: z.number().int().min(0).max(1000),
  pdfDownload: z.boolean(),
});
export type DeviceInfoLayer = z.infer<typeof deviceInfoLayerSchema>;

export const DATA_RANGES = ['1d', '7d', '30d', 'all'] as const;
export const dataLayerSchema = z.object({
  enabled: z.boolean(),
  title: z.string().max(120),
  series: z
    .array(
      z.object({
        id: itemIdSchema,
        binding: sensorBindingSchema,
        color: hexColor,
        axis: z.enum(['left', 'right']),
        label: z.string().max(60).nullable(),
      }),
    )
    .max(8),
  defaultRange: z.enum(DATA_RANGES),
  defaultView: z.enum(['chart', 'table']),
  thresholds: z
    .array(
      z.object({
        id: itemIdSchema,
        label: z.string().max(60),
        value: z.number(),
        axis: z.enum(['left', 'right']),
        kind: z.enum(['warning', 'danger']),
      }),
    )
    .max(8),
});
export type DataLayer = z.infer<typeof dataLayerSchema>;

export const TABLE_STATUS_COLORS = ['#1ECD15', '#0275FF', '#F48200', '#E10202', '#99A3B8'] as const;
const tableRowBase = z.object({ id: itemIdSchema, name: z.string().max(60) });
export const tableRowSchema = z.discriminatedUnion('type', [
  tableRowBase.extend({ type: z.literal('manual'), cells: z.array(z.string().max(40).nullable()) }),
  tableRowBase.extend({ type: z.literal('device'), cells: z.array(sensorBindingSchema.nullable()) }),
  tableRowBase.extend({
    type: z.literal('formula'),
    cells: z.array(z.string().max(200).nullable()),
    rowFormula: z.string().max(200).nullable(),
  }),
  tableRowBase.extend({ type: z.literal('status'), sourceRowId: itemIdSchema.nullable() }),
]);
export type TableRow = z.infer<typeof tableRowSchema>;

export const tableStatusSchema = z.object({
  id: itemIdSchema,
  name: z.string().max(40),
  description: z.string().max(200),
  color: hexColor,
  /** inclusive */
  min: z.number().nullable(),
  /** exclusive; null = open ("20 dan yuqori") */
  max: z.number().nullable(),
});
export type TableStatus = z.infer<typeof tableStatusSchema>;

export const tableConfigSchema = z.object({
  name: z.string().max(120),
  columnCount: z.number().int().min(1).max(12),
  cornerLabel: z.string().max(40),
  columnLabels: z.array(z.string().max(20)).max(12),
  rows: z.array(tableRowSchema).max(50),
  statuses: z.array(tableStatusSchema).max(10),
  autoColor: z.boolean(),
});
export type TableConfig = z.infer<typeof tableConfigSchema>;

export const tableLayerSchema = z.object({
  enabled: z.boolean(),
  title: z.string().max(120),
  mode: z.enum(['deviceList', 'custom']),
  custom: tableConfigSchema.nullable(),
});
export type TableLayer = z.infer<typeof tableLayerSchema>;

export const folderDocumentSchema = z
  .object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    drawing: drawingSchema.nullable(),
    pins: z.array(pinSchema).max(1000),
    labelDensity: z.enum(['comfortable', 'compact']),
    layers: z.object({ deviceInfo: deviceInfoLayerSchema, data: dataLayerSchema, table: tableLayerSchema }),
  })
  .superRefine((doc, ctx) => {
    const ids = new Set<string>();
    doc.pins.forEach((p, i) => {
      if (ids.has(p.id)) ctx.addIssue({ code: 'custom', path: ['pins', i, 'id'], message: 'Takroriy id' });
      ids.add(p.id);
    });
  });
export type FolderDocument = z.infer<typeof folderDocumentSchema>;

/* ───────── 3D scene (per section) ───────── */

export const placement3DSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  height: z.number().min(-500).max(10000),
  heading: z.number().min(-360).max(360),
  pitch: z.number().min(-90).max(90),
  roll: z.number().min(-180).max(180),
  scale: z.number().positive().max(1000),
  clampToGround: z.boolean(),
});
export type Placement3D = z.infer<typeof placement3DSchema>;

export const scene3DDocumentSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  model: z
    .object({
      source: fileRefSchema,
      gltf: fileRefSchema.nullable(),
      status: z.enum(['processing', 'ready', 'failed']),
      error: z.string().max(500).optional(),
    })
    .nullable(),
  placement: placement3DSchema.nullable(),
  anchors: z
    .array(
      z.object({
        id: itemIdSchema,
        pinRef: z.object({ folderId: z.uuid(), pinId: itemIdSchema }).nullable(),
        binding: sensorBindingSchema,
        /** model-local ENU metres */
        local: z.tuple([z.number(), z.number(), z.number()]),
        visible: z.boolean(),
      }),
    )
    .max(1000),
  camera: z
    .object({ lat: z.number(), lng: z.number(), height: z.number(), heading: z.number(), pitch: z.number() })
    .nullable(),
  heightDatum: z.enum(['ellipsoid', 'terrain']),
});
export type Scene3DDocument = z.infer<typeof scene3DDocumentSchema>;

/* ───────── sections (relational rows) ───────── */

export const SECTION_CARD_MODES = ['iconNameCount', 'nameOnly'] as const;
export const SECTION_COUNT_SOURCES = ['auto', 'manual'] as const;

export const createSectionSchema = z.object({
  name: z.string().trim().min(1).max(80),
  icon: iconRefSchema,
  color: hexColor.optional(),
  cardMode: z.enum(SECTION_CARD_MODES).default('iconNameCount'),
  countSource: z.enum(SECTION_COUNT_SOURCES).default('auto'),
  manualCount: z.number().int().min(0).max(1_000_000).nullable().default(null),
  heading: z.string().trim().max(160).nullable().default(null),
});
export const updateSectionSchema = patchSchema(createSectionSchema).extend({
  hidden: z.boolean().optional(),
  defaultView: z.enum(['drawing', '3d']).optional(),
});
export const reorderSchema = z.object({ ids: z.array(z.uuid()).min(1).max(200) });

export interface SectionDto {
  id: string;
  tenantId: string;
  name: string;
  icon: IconRef;
  color: string;
  cardMode: (typeof SECTION_CARD_MODES)[number];
  countSource: (typeof SECTION_COUNT_SOURCES)[number];
  manualCount: number | null;
  heading: string | null;
  hidden: boolean;
  sortOrder: number;
  defaultView: 'drawing' | '3d';
  autoCount: number;
  folderCount: number;
  version: number;
  updatedAt: string;
}

export const createFolderSchema = z.object({ name: z.string().trim().min(1).max(60) });
export const updateFolderSchema = createFolderSchema.partial();

export interface FolderSummaryDto {
  id: string;
  sectionId: string;
  name: string;
  sortOrder: number;
  isDefault: boolean;
  pinCount: number;
  version: number;
  updatedAt: string;
}
export interface FolderDto extends FolderSummaryDto {
  document: FolderDocument;
}

export interface DashboardDto {
  id: string;
  tenantId: string;
  title: string;
  document: DashboardDocument;
  version: number;
  updatedAt: string;
  updatedBy: { id: string; fullName: string } | null;
}

/** `GET /tenants/:t/sensors` row — a sensor (indicator) of the registry with its latest live value. */
export interface SensorDto {
  id: string;
  deviceId: string;
  deviceCode: string;
  deviceName: string;
  deviceType: string | null;
  code: string;
  name: string;
  unit: string;
  decimals: number;
  value: number | null;
  status: SensorStatus;
  ts: string | null;
}

export interface Scene3DDto {
  sectionId: string;
  document: Scene3DDocument;
  version: number;
  updatedAt: string;
}

/** PUT body for every versioned document (dashboard, folder, scene). 409 on a stale `baseVersion`. */
export const saveDocumentSchema = <T extends z.ZodType>(document: T) =>
  z.object({ document, baseVersion: z.number().int().positive() });

export interface VersionConflict {
  code: 'version_conflict';
  currentVersion: number;
  updatedAt: string;
  updatedBy: { id: string; fullName: string } | null;
}

/* ───────── defaults (§9.3) ───────── */

export const defaultBackground = (): DashboardBackground => ({ media: null, opacity: 1, scale: 1 });

export const emptyDashboard = (): DashboardDocument => ({
  schemaVersion: SCHEMA_VERSION,
  stage: { width: STAGE.width, height: STAGE.height },
  background: defaultBackground(),
  elements: [
    {
      id: 'section-grid',
      type: 'sectionGrid',
      name: "Bo'limlar",
      x: 52,
      y: 859,
      hidden: false,
      locked: false,
      dock: 'bottom',
      width: 1816,
      columns: 6,
      // 14:7285 / 317:6923 "Glass Card" grid: 84 px cards, 14 px gaps
      gap: 14,
      cardHeight: 84,
    },
  ],
});

export const emptyFolderDocument = (): FolderDocument => ({
  schemaVersion: SCHEMA_VERSION,
  drawing: null,
  pins: [],
  labelDensity: 'comfortable',
  layers: {
    deviceInfo: {
      enabled: false,
      title: '',
      media: [],
      collage: 'grid',
      description: { type: 'doc', content: [] },
      descriptionChars: 0,
      pdfDownload: false,
    },
    data: { enabled: false, title: 'Qurilma grafigi', series: [], defaultRange: '7d', defaultView: 'chart', thresholds: [] },
    table: { enabled: false, title: 'Jadval', mode: 'deviceList', custom: null },
  },
});

export const emptyScene = (): Scene3DDocument => ({
  schemaVersion: SCHEMA_VERSION,
  model: null,
  placement: null,
  anchors: [],
  camera: null,
  heightDatum: 'terrain',
});

/** Default element factories used by the editor palette (§9.3). Position is set by the caller. */
export const ELEMENT_DEFAULTS = {
  clock: { scale: 1, showSeconds: true },
  date: { scale: 1, format: 'd MMMM yyyy' as const },
  indicator: { scale: 1, icon: 'dashboard-line', label: '', source: { kind: 'manual' as const, value: '' }, unit: '' },
  title: { title: '', titleSize: 24, subtitle: '', subtitleSize: 13, showAccentBar: true },
  marker: { label: 'Belgi', scale: 1, linkSectionId: null },
  image: { media: null, aspect: '16:9' as const, width: 240, zoom: 1 },
  gif: { media: null, aspect: '16:9' as const, width: 240, zoom: 1 },
  video: { media: null, aspect: '16:9' as const, width: 240, zoom: 1, autoplayMuted: false, loop: true },
} as const;
